/**
 * Future-skill registry.
 *
 * Course lessons intentionally use specific tags ("cells", "data-ethics",
 * "policy-analysis") while the child-facing pathway system needs broader,
 * stable identity skills ("observation", "ethics", "civic-reasoning").
 * This file keeps that translation explicit instead of scattering one-off
 * slug fixes through stores and UI components.
 */

const FUTURE_SKILL_ALIASES: Record<string, string> = {
  'causal-reasoning': 'cause-and-effect',
  'ethical-reasoning': 'ethics',
  'evidence-based-argument': 'argument-from-evidence',
};

const FUTURE_SKILL_LABELS: Record<string, string> = {
  '3d-modeling': '3D Modeling',
  'aed-awareness': 'AED Awareness',
  'ai': 'AI',
  'ai-literacy': 'AI Literacy',
  'apis': 'APIs',
  'argument-from-evidence': 'Argument from Evidence',
  'cad': 'CAD',
  'cause-and-effect': 'Cause & Effect',
  'data-ethics': 'Data Ethics',
  'data-literacy': 'Data Literacy',
  'design-thinking': 'Design Thinking',
  'earth-science': 'Earth Science',
  'economic-thinking': 'Economic Thinking',
  'environmental-literacy': 'Environmental Literacy',
  'global-citizenship': 'Global Citizenship',
  'human-centered-design': 'Human-Centered Design',
  'inclusive-design': 'Inclusive Design',
  'media-literacy': 'Media Literacy',
  'pattern-recognition': 'Pattern Recognition',
  'pitch-presentation': 'Pitch Presentation',
  'policy-analysis': 'Policy Analysis',
  'profit-loss': 'Profit & Loss',
  'source-evaluation': 'Source Evaluation',
  'systems-thinking': 'Systems Thinking',
  'ux': 'UX',
  'visual-literacy': 'Visual Literacy',
  'what-if-scenarios': 'What-If Scenarios',
};

const EXACT_PATHWAY_ROLLUPS: Record<string, string[]> = {
  accessibility: ['design-thinking', 'ethics'],
  accountability: ['ethics', 'decision-making'],
  adaptation: ['scientific-method', 'classification'],
  agriculture: ['environmental-literacy', 'systems-thinking'],
  'ai-literacy': ['critical-thinking', 'data-analysis'],
  analysis: ['critical-thinking'],
  anthropology: ['cultural-literacy', 'perspective-taking'],
  argument: ['argument-from-evidence'],
  argumentation: ['argument-from-evidence'],
  architecture: ['design-thinking', 'systems-thinking'],
  astronomy: ['observation', 'scientific-method'],
  biology: ['classification', 'observation'],
  budgeting: ['counting-money', 'decision-making'],
  'built-environment': ['systems-thinking', 'design-thinking'],
  'cause-and-effect': ['critical-thinking', 'prediction'],
  cells: ['classification', 'observation'],
  chemistry: ['experimentation', 'scientific-method'],
  civics: ['civic-reasoning', 'civic-engagement'],
  climate: ['systems-thinking', 'environmental-literacy'],
  'climate-literacy': ['systems-thinking', 'environmental-literacy'],
  collaboration: ['collaboration'],
  communication: ['communication'],
  community: ['collaboration', 'civic-engagement'],
  comparison: ['critical-thinking'],
  conservation: ['conservation'],
  culture: ['cultural-literacy', 'perspective-taking'],
  data: ['data-analysis'],
  'data-collection': ['data-analysis', 'observation'],
  'data-literacy': ['data-analysis'],
  design: ['design-thinking'],
  ecology: ['environmental-literacy', 'systems-thinking'],
  economics: ['economic-thinking', 'decision-making'],
  empathy: ['perspective-taking'],
  energy: ['systems-thinking', 'scientific-method'],
  engineering: ['systems-thinking', 'design-thinking'],
  ethics: ['ethics'],
  evidence: ['argument-from-evidence', 'critical-thinking'],
  fieldwork: ['observation', 'source-evaluation'],
  'global-citizenship': ['cultural-literacy', 'civic-engagement'],
  health: ['self-awareness'],
  history: ['historical-empathy', 'cultural-literacy'],
  infrastructure: ['systems-thinking'],
  justice: ['ethics', 'civic-reasoning'],
  law: ['civic-reasoning', 'argument-from-evidence'],
  markets: ['economic-thinking', 'entrepreneurship'],
  modeling: ['data-analysis', 'prediction'],
  observation: ['observation'],
  patterns: ['pattern-recognition'],
  physics: ['scientific-method', 'prediction'],
  planning: ['sequencing', 'decision-making'],
  policy: ['civic-reasoning'],
  'policy-analysis': ['civic-reasoning', 'decision-making'],
  probability: ['mathematical-reasoning', 'prediction'],
  'product-thinking': ['product-design', 'design-thinking'],
  presentation: ['communication', 'pitch-presentation'],
  prototyping: ['design-thinking', 'failure-recovery'],
  research: ['source-evaluation', 'critical-thinking'],
  respect: ['perspective-taking', 'collaboration'],
  rights: ['civic-reasoning', 'ethics'],
  risk: ['decision-making'],
  'risk-analysis': ['decision-making', 'critical-thinking'],
  robotics: ['systems-thinking', 'design-thinking'],
  'scientific-thinking': ['scientific-method', 'critical-thinking'],
  sociology: ['cultural-literacy', 'perspective-taking'],
  sustainability: ['environmental-literacy', 'conservation'],
  systems: ['systems-thinking'],
  technology: ['systems-thinking', 'critical-thinking'],
  testing: ['experimentation', 'failure-recovery'],
  tradeoffs: ['decision-making', 'critical-thinking'],
};

const KEYWORD_PATHWAY_ROLLUPS: { needles: string[]; targets: string[] }[] = [
  {
    needles: ['ethic', 'fairness', 'bias', 'consent', 'privacy', 'rights', 'justice'],
    targets: ['ethics'],
  },
  {
    needles: ['data', 'statistic', 'probability', 'graph', 'measurement', 'average', 'model'],
    targets: ['data-analysis'],
  },
  {
    needles: ['science', 'biology', 'chemistry', 'physics', 'geology', 'astronomy', 'planet', 'star', 'earth', 'climate', 'ecology'],
    targets: ['scientific-method'],
  },
  {
    needles: ['system', 'infrastructure', 'network', 'grid', 'supply-chain', 'feedback'],
    targets: ['systems-thinking'],
  },
  {
    needles: ['design', 'prototype', 'product', 'architecture', 'accessibility', 'robotics', 'maker', 'fabrication'],
    targets: ['design-thinking'],
  },
  {
    needles: ['civic', 'policy', 'government', 'democracy', 'law', 'public', 'institution'],
    targets: ['civic-reasoning'],
  },
  {
    needles: ['culture', 'history', 'geography', 'migration', 'worldview', 'anthropology', 'sociology'],
    targets: ['cultural-literacy'],
  },
  {
    needles: ['market', 'business', 'economy', 'economic', 'entrepreneur', 'money', 'pricing', 'budget'],
    targets: ['economic-thinking'],
  },
  {
    needles: ['story', 'journalism', 'speaking', 'presentation', 'theater', 'media', 'writing'],
    targets: ['communication'],
  },
  {
    needles: ['health', 'body', 'nutrition', 'emotion', 'psychology', 'safety', 'first-aid'],
    targets: ['self-awareness'],
  },
];

export function canonicalizeFutureSkill(skillId: string): string {
  return FUTURE_SKILL_ALIASES[skillId] ?? skillId;
}

export function normalizeFutureSkills(skills: string[]): string[] {
  return [...new Set(skills.map(canonicalizeFutureSkill).filter(Boolean))];
}

export function getFutureSkillPathwayKeys(skillId: string): string[] {
  const canonical = canonicalizeFutureSkill(skillId);
  const keys = new Set<string>([canonical]);

  for (const target of EXACT_PATHWAY_ROLLUPS[canonical] ?? []) {
    keys.add(target);
  }

  for (const rule of KEYWORD_PATHWAY_ROLLUPS) {
    if (rule.needles.some(needle => canonical.includes(needle))) {
      for (const target of rule.targets) keys.add(target);
    }
  }

  return [...keys];
}

export function getFutureSkillLabel(skillId: string): string {
  const canonical = canonicalizeFutureSkill(skillId);
  if (FUTURE_SKILL_LABELS[canonical]) return FUTURE_SKILL_LABELS[canonical];

  return canonical
    .split('-')
    .filter(Boolean)
    .map(part => {
      if (/^\d+d$/i.test(part)) return part.toUpperCase();
      if (part.length <= 3 && ['ai', 'api', 'apis', 'cad', 'ux'].includes(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}
